/**
 * Order normalization and payload building utilities
 * Ensures consistent order structures across all checkout flows
 * (CartPage / PlaceOrderModal, DirectOrderModal, QuickBuyModal).
 */

/**
 * Format a single product or cart item into a comprehensive order item record.
 * Includes both camelCase and snake_case keys for seamless backend compatibility.
 */
export function normalizeOrderItem(item) {
    if (!item) return null;

    const prodId = String(
        item.productId ||
        item.product_id ||
        item.id ||
        item._id ||
        ''
    );

    const mrp = Number(item.mrp ?? item.price ?? 0);
    const unitPrice = Number(
        item.cashPrice ??
        item.cash_price ??
        item.unitPrice ??
        item.unit_price ??
        item.price ??
        (mrp > 0 ? mrp * 0.95 : 0)
    ) || mrp;

    const qty = Math.max(1, parseInt(item.quantity, 10) || 1);
    const lineTotal = Number(
        item.totalPrice ??
        item.total_price ??
        (unitPrice * qty)
    );

    return {
        productId: prodId,
        product_id: prodId,
        name: item.name || 'Genuine Home Appliance',
        brand: typeof item.brand === 'object' ? item.brand?.name : (item.brand || 'Rahber Corporation'),
        sku: item.sku || item.model || item.model_number || '',
        image: item.image || '',
        quantity: qty,
        unitPrice: unitPrice,
        unit_price: unitPrice,
        price: unitPrice,
        totalPrice: lineTotal,
        total_price: lineTotal,
        mrp: mrp > 0 ? mrp : unitPrice,
        payment_plan: item.payment_plan || 'cash',
        plan_details: item.plan_details || '100% Cash Purchase',
    };
}

/**
 * Build a complete, structured order payload ready for backend API submission.
 */
export function buildOrderPayload({
    customer_name,
    customer_phone,
    customer_whatsapp,
    customer_email,
    delivery_city = 'Lahore',
    lahore_area = '',
    delivery_address = '',
    delivery_time = 'Standard (Same Day / Next Day)',
    payment_method = 'Advance Bank Transfer (Meezan / HBL)',
    order_type = 'cash',
    notes = '',
    items = [],
    total_amount = 0,
    subtotal = 0,
    discount_amount = 0,
}) {
    const formattedItems = (Array.isArray(items) ? items : [])
        .map(normalizeOrderItem)
        .filter(Boolean);

    const calculatedTotal = formattedItems.reduce(
        (sum, it) => sum + (it.totalPrice || 0),
        0
    );

    const finalTotal = Number(total_amount) > 0 ? Number(total_amount) : calculatedTotal;

    const fullAddress =
        delivery_city === 'Lahore' && lahore_area && !delivery_address.includes(lahore_area)
            ? `${delivery_address.trim()}, ${lahore_area}, Lahore`
            : delivery_city !== 'Lahore' && !delivery_address.includes(delivery_city)
            ? `${delivery_address.trim()}, ${delivery_city}`
            : delivery_address.trim();

    const orderNumber = 'ORD-' + Math.floor(100000 + Math.random() * 900000);

    return {
        order_number: orderNumber,
        customer_name: (customer_name || '').trim(),
        customer_phone: (customer_phone || '').trim(),
        customer_whatsapp: (customer_whatsapp || customer_phone || '').trim(),
        customer_email: (customer_email || '').trim(),
        delivery_city: delivery_city || 'Lahore',
        delivery_area: lahore_area || '',
        delivery_address: fullAddress,
        delivery_time: delivery_time || 'Standard (Same Day / Next Day)',
        payment_method: payment_method || 'Advance Bank Transfer (Meezan / HBL)',
        order_type: order_type || 'cash',
        notes: notes ? notes.trim() : `Delivery Preference: ${delivery_time}`,
        items: formattedItems,
        total_items: formattedItems.reduce((acc, it) => acc + it.quantity, 0),
        total_amount: finalTotal,
        subtotal: subtotal > 0 ? subtotal : finalTotal,
        discount_amount: discount_amount || 0,
        currency: 'PKR',
        status: 'Pending',
        created_at: new Date().toISOString(),
    };
}
